import { ReactNode } from "react";
import { BsCheckCircleFill } from "react-icons/bs";
import { IdentityCode } from "../../../../../models/payment-setup.dto";
import {
	useGetAvailablePaymentDestinations,
	useGetMerchantCategories,
	useGetPaymentCompanyTypes,
	useGetPaymentRegions,
	useGetPaymentTiers,
} from "../../../../../services/miscellaneous-payment.service";
import {
	MerchantCategory,
	PaymentDestination,
	PaymentSetupFormValues,
	PaymentTier,
	getDocumentLabel,
	getTierRange,
	splitCategories,
	toList,
} from "./helpers";

const Row = ({ label, value }: { label: string; value?: ReactNode }) => (
	<div className="grid grid-cols-[200px_1fr] gap-4 py-2 text-sm">
		<dt className="text-gray-500">{label}</dt>
		<dd className="text-gray-800 break-words">{value || <span className="text-gray-400">—</span>}</dd>
	</div>
);

const Section = ({
	title,
	onEdit,
	children,
}: {
	title: string;
	onEdit?: () => void;
	children: ReactNode;
}) => (
	<section className="rounded-lg border border-gray-200 px-5 py-4">
		<div className="flex justify-between items-center mb-2">
			<h4 className="font-semibold text-sm text-charcoal">{title}</h4>
			{onEdit && (
				<button type="button" onClick={onEdit} className="text-xs text-primary-blue">
					Edit
				</button>
			)}
		</div>
		<dl className="divide-y divide-gray-100">{children}</dl>
	</section>
);

const FileLink = ({ url, label = "View" }: { url?: string; label?: string }) =>
	url ? (
		<a href={url} target="_blank" rel="noreferrer" className="text-primary-blue">
			{label}
		</a>
	) : null;

const formatDate = (value?: string) =>
	value ? new Date(value).toLocaleDateString(undefined, { dateStyle: "medium" }) : undefined;

interface RequestSummaryProps {
	values: PaymentSetupFormValues;
	/** Called with the wizard step index to jump back and edit a section */
	onEditStep?: (step: number) => void;
}

const RequestSummary = ({ values, onEditStep }: RequestSummaryProps) => {
	const { data: regionsResponse } = useGetPaymentRegions();
	const { data: destinationsResponse } = useGetAvailablePaymentDestinations();
	const { data: tiersResponse } = useGetPaymentTiers();
	const { data: categoriesResponse } = useGetMerchantCategories({ country: values.country });

	const regions = toList<{
		regionId: string;
		region: string;
		districts?: { districtId: string; districtName: string }[];
	}>(regionsResponse);
	const destinations = toList<PaymentDestination>(destinationsResponse);
	const tiers = toList<PaymentTier>(tiersResponse);
	const categories = toList<MerchantCategory>(categoriesResponse);
	const { data: companyTypesResponse } = useGetPaymentCompanyTypes();
	const classification = toList<{ id?: string; name: string }>(companyTypesResponse).find(
		(type) => type.id != null && String(type.id) === String(values.countryMerchantCategoryId),
	);

	const address = values.officeAddress;
	const region = regions.find((item) => item.regionId === address.officeRegion);
	const district = region?.districts?.find(
		(item) => item.districtId === address.officeDistrict,
	);
	const tier = tiers.find((item) => item.name === values.tier);
	const categoryLabels = splitCategories(values.companyCategories).map(
		(name) => categories.find((item) => item.name === name)?.label || name,
	);
	const edit = (step: number) => (onEditStep ? () => onEditStep(step) : undefined);

	return (
		<div className="space-y-4">
			<Section title="Business information" onEdit={edit(0)}>
				<Row
					label="Company logo"
					value={
						values.companyLogo && (
							<img
								src={values.companyLogo}
								alt="Company logo"
								className="h-10 w-10 object-contain rounded border"
							/>
						)
					}
				/>
				<Row label="Registered name" value={values.companyName} />
				<Row label="Trading name" value={values.tradeName} />
				<Row label="Alias" value={values.alias} />
				<Row label="TIN" value={values.taxIdentificationNumber} />
				<Row
					label="Tier"
					value={
						values.tier &&
						`${values.tier}${tier && getTierRange(tier) ? ` (${getTierRange(tier)})` : ""}`
					}
				/>
				<Row label="Country" value={values.country} />
				<Row
					label="Business categories"
					value={categoryLabels.join(", ")}
				/>
				<Row
					label="Classification"
					value={classification?.name || values.countryMerchantCategoryId}
				/>
				<Row label="Type of company" value={values.typeOfCompany} />
				<Row label="Registration number" value={values.companyRegistrationNumber} />
				<Row label="VAT number" value={values.vatRegistrationNumber} />
				<Row label="Incorporated" value={formatDate(values.dateOfIncorporation)} />
				<Row label="Started trading" value={formatDate(values.dateOfCommencement)} />
			</Section>

			<Section title="Contact & address" onEdit={edit(1)}>
				<Row label="Primary contact" value={`${values.otherNames} ${values.surname}`.trim()} />
				<Row label="Email" value={values.email} />
				<Row label="Phone" value={values.phoneNumber && `+${values.phoneNumber}`} />
				<Row
					label="Office address"
					value={[
						address.officeAddress,
						address.officeCity,
						district?.districtName,
						region?.region,
						address.officeGpsAddress,
					]
						.filter(Boolean)
						.join(", ")}
				/>
				<Row label="Premises" value={address.officeOwnership} />
				{values.contacts.map((contact, index) => (
					<Row
						key={index}
						label={`${contact.name} contact`}
						value={`${contact.email} · +${contact.phone}`}
					/>
				))}
			</Section>

			<Section title="Directors" onEdit={edit(2)}>
				{values.companyDirectors.map((director, index) => (
					<Row
						key={index}
						label={`${director.otherNames} ${director.lastName}`.trim() || `Director ${index + 1}`}
						value={
							<span className="flex flex-wrap items-center gap-x-3 gap-y-1">
								{director.identityCode === IdentityCode.ghana_card && (
									<>
										<span>Ghana Card {director.identity}</span>
										{director.verificationId ? (
											<span className="flex items-center gap-1 text-green-700 text-xs">
												<BsCheckCircleFill /> Verified
											</span>
										) : (
											<span className="text-amber-700 text-xs">Not verified</span>
										)}
									</>
								)}
								{director.identityCode === IdentityCode.passport && (
									<span>
										Passport · <FileLink url={director.identity} />
									</span>
								)}
								{[director.email, director.phoneNumber && `+${director.phoneNumber}`]
									.filter(Boolean)
									.map((item) => (
										<span key={item} className="text-gray-500">
											{item}
										</span>
									))}
							</span>
						}
					/>
				))}
			</Section>

			<Section title="Documents" onEdit={edit(3)}>
				{Object.entries(values.documents).filter(([, url]) => !!url).length ? (
					Object.entries(values.documents)
						.filter(([, url]) => !!url)
						.map(([code, url]) => (
							<Row key={code} label={getDocumentLabel(code)} value={<FileLink url={url} />} />
						))
				) : (
					<Row label="Uploaded" value={undefined} />
				)}
			</Section>

			<Section title="Products & settlement" onEdit={edit(4)}>
				{values.products.length ? (
					values.products.map((product, index) => {
						const details = product.destinationDetails;
						const destination = destinations.find(
							(item) => item.destinationId === details.destinationId,
						);
						return (
							<Row
								key={index}
								label={product.name || product.code}
								value={
									<span>
										{destination?.name || "Bank"} · {details.destinationBranchName} ·{" "}
										{details.destinationAccountNumber} ({details.destinationAccountName})
										<span className="block text-xs text-gray-500 mt-0.5">
											{product.description}
										</span>
									</span>
								}
							/>
						);
					})
				) : (
					<Row label="Products" value="None yet. You can add products later." />
				)}
			</Section>
		</div>
	);
};

export default RequestSummary;
